'use client';

import axiosInstance, { endpoints } from 'src/lib/axios';

import { registreraFel } from './felbutik';
import { ärAllaId } from 'src/context/app/alla-organisationer';
import { rapporteraTillCrashlytics } from 'src/lib/crashlytics';

/** Fel vi sett den senaste stunden, så en renderingsloop inte spammar servern. */
const nyligenRapporterade = new Map<string, number>();
const DÄMPNING_MS = 60_000;
const MAX_PER_MINUT = 5;
let fönsterStart = Date.now();
let antalIFönstret = 0;

function fårSkicka(nyckel: string): boolean {
  const nu = Date.now();

  if (nu - fönsterStart > DÄMPNING_MS) {
    fönsterStart = nu;
    antalIFönstret = 0;
  }
  if (antalIFönstret >= MAX_PER_MINUT) {
    return false;
  }

  const senast = nyligenRapporterade.get(nyckel);
  if (senast && nu - senast < DÄMPNING_MS) {
    return false;
  }

  nyligenRapporterade.set(nyckel, nu);
  antalIFönstret += 1;
  return true;
}

/**
 * Skickar en krasch från webbläsaren till felloggen och returnerar referensen.
 *
 * Sväljer sina egna fel med flit: misslyckas rapporteringen är det inget användaren kan göra
 * något åt, och ett fel här skulle bara ersätta det ursprungliga felet med ett sämre.
 */
export async function rapporteraKlientfel(fel: Error, extra?: { orgId?: string }): Promise<string | undefined> {
  try {
    const nyckel = `${fel?.name}:${fel?.message}`;
    if (!fårSkicka(nyckel)) {
      return undefined;
    }

    const orgId = extra?.orgId ?? läsOrgIdUrLagring();
    const sökväg = typeof window !== 'undefined' ? window.location.pathname : null;

    // Rapportera även till Firebase Crashlytics när vi kör i mobilapp
    void rapporteraTillCrashlytics(fel, { orgId, sökväg });

    const svar = await axiosInstance.post(endpoints.fel.klientfel, {
      feltyp: fel?.name || 'Error',
      felmeddelande: fel?.message ?? String(fel),
      stacktrace: fel?.stack ?? null,
      klientSökväg: sökväg,
      korrelationsId: null,
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? null,
      orgId,
    });

    const referens: string | undefined = svar?.data?.referens;

    registreraFel({
      referens,
      meddelande: 'Ett oväntat fel inträffade i appen.',
      tidpunkt: new Date(),
      sökväg: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });

    return referens;
  } catch {
    return undefined;
  }
}

function läsOrgIdUrLagring(): string | null {
  try {
    const rå = localStorage.getItem('app-context');
    if (!rå) return null;
    const id = JSON.parse(rå)?.organisation?.id;
    // Hubbläget är en attrapp-organisation, inte ett UUID — servern skulle avvisa den.
    return id && !ärAllaId(id) ? id : null;
  } catch {
    return null;
  }
}
